import { Injectable, signal, computed } from '@angular/core';

export type Language = 'de' | 'en' | 'fr' | 'it' | 'nl';

interface Translations {
    [key: string]: {
        [K in Language]: string;
    };
}

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    private readonly STORAGE_KEY = 'portal_lang';

    // Current language signal
    private readonly currentLangSignal = signal<Language>(this.getInitialLanguage());

    readonly currentLang = this.currentLangSignal.asReadonly();

    private readonly translations: Translations = {
        login_title: {
            de: 'Melden Sie sich an, um auf Ihr Konto zuzugreifen',
            en: 'Sign in to access your account',
            fr: 'Connectez-vous pour accéder à votre compte',
            it: 'Accedi per accedere al tuo account',
            nl: 'Log in om toegang te krijgen tot uw account',
        },
        welcome_openolat: {
            de: 'Willkommen auf der OpenOlat Lernplattform von {{school}}',
            en: 'Welcome to the OpenOlat learning platform of {{school}}',
            fr: 'Bienvenue sur la plateforme d\'apprentissage OpenOlat de {{school}}',
            it: 'Benvenuti sulla piattaforma di apprendimento OpenOlat di {{school}}',
            nl: 'Welkom op het OpenOlat leerplatform van {{school}}',
        },
        label_username: {
            de: 'Benutzername oder E-Mail',
            en: 'Username or Email',
            fr: 'Nom d\'utilisateur ou e-mail',
            it: 'Nome utente o e-mail',
            nl: 'Gebruikersnaam of e-mail',
        },
        label_password: {
            de: 'Passwort',
            en: 'Password',
            fr: 'Mot de passe',
            it: 'Password',
            nl: 'Wachtwoord',
        },
        placeholder_username: {
            de: 'Geben Sie Ihren Benutzernamen ein',
            en: 'Enter your username',
            fr: 'Entrez votre nom d\'utilisateur',
            it: 'Inserisci il tuo nome utente',
            nl: 'Voer uw gebruikersnaam in',
        },
        placeholder_password: {
            de: 'Geben Sie Ihr Passwort ein',
            en: 'Enter your password',
            fr: 'Entrez votre mot de passe',
            it: 'Inserisci la tua password',
            nl: 'Voer uw wachtwoord in',
        },
        btn_signin: {
            de: 'Anmelden',
            en: 'Sign In',
            fr: 'Se connecter',
            it: 'Accedi',
            nl: 'Inloggen',
        },
        btn_vantage_login: {
            de: 'Vantage-Login',
            en: 'Vantage-Login',
            fr: 'Vantage-Login',
            it: 'Vantage-Login',
            nl: 'Vantage-Login',
        },
        link_forgot_password: {
            de: 'Passwort vergessen?',
            en: 'Forgot password?',
            fr: 'Mot de passe oublié ?',
            it: 'Password dimenticata?',
            nl: 'Wachtwoord vergeten?',
        },
        link_request_account: {
            de: 'Konto beantragen',
            en: 'Request account',
            fr: 'Demander un compte',
            it: 'Richiedi un account',
            nl: 'Account aanvragen',
        },
        text_or: {
            de: 'oder',
            en: 'or',
            fr: 'ou',
            it: 'o',
            nl: 'of',
        },
        text_coming_soon: {
            de: 'Demnächst verfügbar ... Anmeldung mit Passkey',
            en: 'Coming soon ... Logon with Passkey',
            fr: 'Bientôt disponible ... Connexion avec Passkey',
            it: 'In arrivo ... Accesso con Passkey',
            nl: 'Binnenkort beschikbaar ... Inloggen met Passkey',
        },
        msg_empty_fields: {
            de: 'Bitte geben Sie sowohl den Benutzernamen als auch das Passwort ein.',
            en: 'Please enter both username and password.',
            fr: 'Veuillez saisir le nom d\'utilisateur et le mot de passe.',
            it: 'Inserisci sia il nome utente che la password.',
            nl: 'Voer zowel gebruikersnaam als wachtwoord in.',
        },
        msg_login_failed: {
            de: 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.',
            en: 'Login failed. Please check your credentials.',
            fr: 'Échec de la connexion. Veuillez vérifier vos identifiants.',
            it: 'Accesso fallito. Controlla le tue credenziali.',
            nl: 'Inloggen mislukt. Controleer uw inloggegevens.',
        },
        msg_azure_failed: {
            de: 'Vantage-Login fehlgeschlagen. Bitte versuchen Sie es erneut.',
            en: 'Vantage login failed. Please try again.',
            fr: 'Échec de la connexion Vantage. Veuillez réessayer.',
            it: 'Accesso Vantage fallito. Riprova.',
            nl: 'Vantage-inloggen mislukt. Probeer het opnieuw.',
        },
        msg_password_wrong: {
            de: 'Passwort falsch.',
            en: 'Password wrong.',
            fr: 'Mot de passe incorrect.',
            it: 'Password errata.',
            nl: 'Wachtwoord onjuist.',
        },
        msg_username_unknown: {
            de: 'Benutzername unbekannt.',
            en: 'Username unknown.',
            fr: 'Nom d\'utilisateur inconnu.',
            it: 'Nome utente sconosciuto.',
            nl: 'Gebruikersnaam onbekend.',
        },
        msg_wait: {
            de: 'Bitte warten Sie {{seconds}}s...',
            en: 'Please wait {{seconds}}s...',
            fr: 'Veuillez patienter {{seconds}}s...',
            it: 'Attendi {{seconds}}s...',
            nl: 'Even geduld {{seconds}}s...',
        },
        footer_rights: {
            de: 'Alle Rechte vorbehalten.',
            en: 'All rights reserved.',
            fr: 'Tous droits réservés.',
            it: 'Tutti i diritti riservati.',
            nl: 'Alle rechten voorbehalten.',
        },
        footer_privacy: {
            de: 'Datenschutz',
            en: 'Privacy',
            fr: 'Confidentialité',
            it: 'Privacy',
            nl: 'Privacy',
        },
        footer_terms: {
            de: 'AGB',
            en: 'Terms',
            fr: 'Conditions',
            it: 'Termini',
            nl: 'Voorwaarden',
        },
        footer_contact: {
            de: 'Kontakt',
            en: 'Contact',
            fr: 'Contact',
            it: 'Contatto',
            nl: 'Contact',
        }
    };

    /**
     * Translates a key based on the current language
     */
    t(key: string, params?: { [key: string]: any }): string {
        const lang = this.currentLangSignal();
        const translation = this.translations[key]?.[lang] || this.translations[key]?.['de'] || key;

        if (params) {
            let result = translation;
            Object.keys(params).forEach(p => {
                result = result.replace(`{{${p}}}`, params[p]);
            });
            return result;
        }

        return translation;
    }

    /**
     * Sets the current language and stores it
     */
    setLanguage(lang: Language): void {
        this.currentLangSignal.set(lang);
        localStorage.setItem(this.STORAGE_KEY, lang);
        document.documentElement.lang = lang;
    }

    /**
     * Gets initial language from storage or browser settings
     */
    private getInitialLanguage(): Language {
        // 1. Check local storage
        const stored = localStorage.getItem(this.STORAGE_KEY) as Language;
        if (this.isValidLanguage(stored)) {
            return stored;
        }

        // 2. Check browser language
        const browserLang = navigator.language.split('-')[0].toLowerCase() as Language;
        if (this.isValidLanguage(browserLang)) {
            return browserLang;
        }

        // 3. Fallback to German
        return 'de';
    }

    private isValidLanguage(lang: any): lang is Language {
        return ['de', 'en', 'fr', 'it', 'nl'].includes(lang);
    }
}
